import { defineStore } from 'pinia';
import { i18n } from 'src/boot/i18n';
import type { dynamicObj } from 'src/pages/assets';
import { ApiExceptions } from 'src/exceptions/index';
import { convertLabel } from 'src/utils/ms_utlis';
import { injectCalculatedFields } from 'src/utils/fieldSettingsHelper';
import { useSettingStore } from 'src/store/Settings/setting.store';
import {
  docFieldsApiService,
  masterDataApiService,
  documentTypeApiService,
} from 'src/services';
import { DocType } from 'src/sdk/api';

type DocTypeField = {
  field_name?: string;
  title?: string;
  field_type?: string;
  [key: string]: any;
};

type DocTypeFieldGroup = {
  rows?: unknown;
  [key: string]: unknown;
};

function asFieldGroups(value: unknown): DocTypeFieldGroup[] {
  if (!Array.isArray(value)) return [];

  return value.filter(
    (group): group is DocTypeFieldGroup =>
      group !== null && typeof group === 'object',
  );
}

function asFieldRows(value: unknown): DocTypeField[][] {
  if (!Array.isArray(value)) return [];

  return value
    .filter((row): row is unknown[] => Array.isArray(row))
    .map((row) =>
      row.filter(
        (field): field is DocTypeField =>
          field !== null && typeof field === 'object',
      ),
    );
}

// DK-10: read the org pref synchronously. Default OFF when missing.
function isInvoiceCalculationEnabled(): boolean {
  try {
    const v =
      useSettingStore().org_based_settings?.[
        'INVOICE_CALCULATION_FIELDS_ENABLED'
      ];
    return String(v ?? 'false') === 'true';
  } catch {
    return false;
  }
}

export const useDocTypeFieldsStore = defineStore('doc_type_fields', {
  state: () => ({
    fields_doc_type_list: {} as dynamicObj,
    processing: false,
    lookup_configurations: [] as any,
    lookup_fields: {} as any,
    validation_fields: [] as any,
    validation_fields_sorted: [] as any,
    all_validation_fields: [] as any,
    show_configuration_popup: false,
    show_lov_configuration_popup: false,
    show_master_data_popup: false,
    show_lov_master_data_popup: false,
    fields_lookup_mapping: {} as dynamicObj,
    tableColumnList: {} as any,
    tableColumns: [] as any,
    docTypesFieldsGroup: {} as dynamicObj,
    fields: {} as any,
  }),
  getters: {
    getFieldsByDocType: (state: any) => (doc_type: string) => {
      if (
        !Object.prototype.hasOwnProperty.call(
          state.fields_doc_type_list,
          doc_type,
        )
      ) {
        return {};
      }

      const fields = {} as dynamicObj;

      for (const group of asFieldGroups(state.fields_doc_type_list[doc_type])) {
        for (const row of asFieldRows(group.rows)) {
          for (const field of row) {
            if (!field.field_name) continue;

            fields[field.field_name] = field;
            fields[field.field_name].selected = false;
          }
        }
      }

      return fields;
    },
    getAllUniqueFieldsWithDocTypes: (state: {
      fields_doc_type_list: Record<string, unknown>;
    }): {
      field_name: string;
      title: string;
      doc_types: string[];
      field_type?: string;
    }[] => {
      let fieldMap = [] as {
        field_name: string;
        title: string;
        doc_types: string[];
        field_type?: string;
      }[];
      Object.keys(state.fields_doc_type_list).forEach((doc_type) => {
        const groups = asFieldGroups(state.fields_doc_type_list[doc_type]);

        groups.forEach((group) => {
          const rows = asFieldRows(group.rows);

          rows.forEach((row) => {
            row.forEach((field) => {
              if (!field.field_name || !field.title) {
                return;
              }
              const existingField = fieldMap.find(
                (item) => item.field_name === field.field_name,
              );
              if (existingField) {
                if (!existingField.doc_types.includes(doc_type)) {
                  existingField.doc_types.push(doc_type);
                }
                return;
              }
              fieldMap.push({
                field_name: field.field_name,
                title: field.title,
                doc_types: [doc_type],
                field_type: field.field_type,
              });
            });
          });
        });
      });
      return fieldMap;
    },
    getFieldsArrayByDocType:
      (state: { fields_doc_type_list: Record<string, unknown> }) =>
      (doc_type: string): { index: number; key: string; name: string }[] => {
        if (
          !Object.prototype.hasOwnProperty.call(
            state.fields_doc_type_list,
            doc_type,
          )
        ) {
          return [];
        }

        let indexCounter = 0;

        return asFieldGroups(state.fields_doc_type_list[doc_type]).flatMap(
          (group) =>
            asFieldRows(group.rows).flatMap((row) =>
              row
                .filter((field) => !!field.field_name && !!field.title)
                .map((field) => ({
                  index: indexCounter++, // Assigns a unique index
                  key: String(field.field_name), // This will be used in insertKey()
                  name: String(field.title), // Displayed in <q-item-section>
                })),
            ),
        );
      },
  },
  actions: {
    async deleteLookupConfiguration(lookup_config_id: string) {
      try {
        this.processing = true;
        const response: dynamicObj =
          await docFieldsApiService.deleteLookupConfiguration(lookup_config_id);
        return response;
      } catch (err) {
        return err;
      } finally {
        this.processing = false;
      }
    },
    async deleteField(lookup_config_id: string, field_config_id: string) {
      try {
        this.processing = true;
        const response: dynamicObj =
          await docFieldsApiService.deleteConfigField(
            lookup_config_id,
            field_config_id,
          );
        return response;
      } catch (err) {
        logError(err);
        return err;
      } finally {
        this.processing = false;
      }
    },
    async UpdateConfiguration(lookup_config_id: string, data: any) {
      try {
        this.processing = true;
        const response: dynamicObj =
          await docFieldsApiService.updateLookupConfiguration(
            data,
            lookup_config_id,
          );
        const item = this.lookup_configurations.find(
          (item: any) => item.id === lookup_config_id,
        );
        if (item) {
          item.doc_type = data.doc_type;
          item.match_all = data.match_all;
          item.lookup_name = data.lookup_name;
          item.conflict_handle = data.conflict_handle;
          item.lookup_dataset_name = data.lookup_dataset_name;
        }

        return response;
      } catch (err) {
        return err;
      } finally {
        this.processing = false;
      }
    },
    async CreateConfiguration(data: any) {
      try {
        this.processing = true;
        const response: dynamicObj =
          await docFieldsApiService.createLookupConfiguration(data);
        //For Reference: To fix the issue mentioned in ticket DOCB-5013 we have to set is_active to true manually here. Otherwise the config is falsely shown as inactive after creation.
        response.data.is_active = true;
        this.lookup_configurations.push(response.data);
        return response;
      } catch (err) {
        return err;
      } finally {
        this.processing = false;
      }
    },
    async CreateFields(lookup_config_id: string, data: any): Promise<any> {
      try {
        this.processing = true;
        logInfo(lookup_config_id, data);
        const api_data: any = {
          field_id: data.validation_field.id,
          lookup_field_name: data.lookup_field.value,
          is_searchable: data.is_searchable,
          auto_trigger: data.auto_trigger,
        };
        if (Object.keys(data.parent_field).length != 0) {
          api_data['parent_field_id'] = data.parent_field.id;
        }

        const response: dynamicObj =
          await docFieldsApiService.createConfigField(
            lookup_config_id,
            api_data,
          );
        return response;
      } catch (err) {
        throw err;
      } finally {
        this.processing = false;
      }
    },
    async UpdateFields(
      lookup_config_id: string,
      field_config_id: string,
      field: any,
    ): Promise<any> {
      try {
        this.processing = true;
        const data: any = {
          lookup_field_name: field.lookup_field.value,
          is_searchable: field.is_searchable,
          auto_trigger: field.auto_trigger,
          field_id: field.validation_field.id,
          search_operator: field.search_operator,
        };
        if (field.parent_field) {
          data['parent_field_id'] = field.parent_field.id;
        }

        const response: dynamicObj = await docFieldsApiService.updateFields(
          data,
          lookup_config_id,
          field_config_id,
        );
        return response;
      } catch (err) {
        throw err;
      } finally {
        this.processing = false;
      }
    },
    async getFieldsForConfiguration(lookup_config_id: string) {
      try {
        this.processing = true;
        await docFieldsApiService
          .getFieldsForConfiguration(lookup_config_id)
          .then((response: any) => {
            this.lookup_fields[lookup_config_id] = response.data;
          });
      } catch (err) {
        logError(err);
      } finally {
        this.processing = false;
      }
    },
    async getLookUpConfigurations(doc_type: string) {
      try {
        this.processing = true;
        await docFieldsApiService
          .getLookUpConfigurations(doc_type)
          .then((response: any) => {
            this.lookup_configurations = response.data;
          });
      } catch (err: any) {
        logError(err);
      } finally {
        this.processing = false;
      }
    },

    async getDataSetFields(data_type: string) {
      try {
        if (this.fields_lookup_mapping.hasOwnProperty(data_type)) {
          return;
        }

        this.fields_lookup_mapping[data_type] = {
          processing: true,
          fields: [],
        };
        const response = await masterDataApiService.getTypeFields(data_type);
        if (!response) {
          throw response;
        }

        const master_data_types_fields = [] as any;
        response.forEach((item: string, index: number) => {
          const ms_field_name = item.toLowerCase();
          let label = convertLabel(item);
          if (item == 'last_modified_on') {
            label = i18n.t(item);
          }

          master_data_types_fields.push(
            Object.assign(
              {},
              {
                value: ms_field_name,
                label: label,
                type: data_type,
              },
            ),
          );
        });
        this.fields_lookup_mapping[data_type].fields = master_data_types_fields;
      } catch (err: any) {
        logError(err);
      } finally {
        this.fields_lookup_mapping[data_type].processing = false;
      }
    },

    async getAllDocTypeFields() {
      try {
        this.processing = true;
        this.fields_doc_type_list = {};

        const response = await docFieldsApiService.getFieldsForAllDocTypes();
        if (response.hasOwnProperty('documents_field_settings')) {
          this.fields_doc_type_list = response.documents_field_settings;
          injectCalculatedFields(
            this.fields_doc_type_list,
            null,
            isInvoiceCalculationEnabled(),
          );
        }
      } catch (err: any) {
        logError(err);
      } finally {
        this.processing = false;
      }
    },
    async loadTableColumnName(table_name: string) {
      try {
        if (
          Object.keys(this.tableColumnList).length > 0 &&
          this.tableColumnList[table_name].length > 0
        ) {
          this.tableColumns = this.tableColumnList[table_name];
          return;
        }

        this.tableColumnList[table_name] = [];
        const response =
          await documentTypeApiService.getDocumentTableColumns(table_name);

        if (response.data && !response.success) {
          throw { message: response.data.message };
        }

        for (const columnObj of response.data) {
          this.tableColumnList[table_name].push({
            value: columnObj.id,
            label: i18n.t(columnObj.column_name),
            title: i18n.t(columnObj.column_name),
            field_name: columnObj.column_name,
            id: columnObj.id,
          });
        }
        this.tableColumns = this.tableColumnList[table_name];
      } catch (err) {
        const msg = ApiExceptions.getErrorMessage(err as dynamicObj);
        logError(err);
        throw { message: msg };
      }
    },

    async getFieldSettingsByType(docType: string) {
      if (this.docTypesFieldsGroup.hasOwnProperty(docType)) {
        return;
      }

      try {
        const data = { doc_type: docType };
        const response = await docFieldsApiService.getFieldSettingsByType(data);
        if (response.field_setting) {
          injectCalculatedFields(
            response.field_setting,
            docType,
            isInvoiceCalculationEnabled(),
          );
          this.docTypesFieldsGroup[docType] = response.field_setting;
        }
      } catch (err) {
        const msg = ApiExceptions.getErrorMessage(err as dynamicObj);
        logError(err);
        throw { message: msg };
      }
    },
  },
});
